/*
Copyright (C) 2026  Integrated Tech9labs Pvt. Ltd.
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import * as React from "react";
import styled, { css, keyframes } from "styled-components";
import { Hourglass, Server, Settings } from "lucide-react";

// The machines show the provider by name now, not its logo on a white plate.
// import EndpointLogos from "@src/components/modules/EndpointModule/EndpointLogos";
import { ensureMarksSprite } from "@src/components/modules/EndpointModule/EndpointLogos/ProviderMark";
import { formatRate, Phase } from "@src/utils/TransferProgress";
import { LIVE, prefersReducedMotion, providerLabel } from "@src/components/modules/LiveModule/liveUtils";

import { buildLut, packetCount, packetSpeed, pointAt, slotVisible } from "./flowMath";
import { ARC_STOPS, arcColourAt, platformColour, platformMark } from "./platforms";

/*
 * The migration animation: the source machine on the left, the target on the right, a curved link
 * between them, and data packets travelling along it at a speed that follows the throughput,
 * denser the more is moving.
 *
 * Each machine carries its platform's mark (the Tech9IQ sprite, or the vendor's wordmark) and is
 * tinted with the platform's brand colour; its three server units have a power light and an
 * activity light that flickers while data is read (source) or written (target). The link is drawn
 * in the Tech9IQ palette, steel → Bright Cyan → Brand Green, with a soft glow, and the packets take
 * the colour of the stretch they are on. The target's units fill bottom-up with the percentage
 * copied, the unit being written carries a moving sheen, and the throughput under the arc pulses.
 *
 * By state: validating / preparing breathe (the link and both ports pulse, a dot ping-pongs);
 * syncing flows; finalising sweeps a light along the link towards the target; completed draws the
 * link solid with a green check and stops; failed breaks the link in red and stops; queued,
 * cancelled and the rest are still. OS preparation spins a gear on the target, deploying builds a
 * check.
 *
 * Packets are moved with requestAnimationFrame on SVG transforms (no React renders per frame); the
 * loop sleeps and the CSS animations pause while the picture is off screen or the tab is hidden,
 * and prefers-reduced-motion gets a still (still colourful) frame.
 */

const W = 640;
const H = 232;
const BODY_W = 132;
const BODY_H = 150;
const TOP = 34;
const SRC_X = 40;
const DST_X = W - 40 - BODY_W;
const PORT_Y = TOP + 76;
const P0 = { x: SRC_X + BODY_W + 8, y: PORT_Y };
const P3 = { x: DST_X - 8, y: PORT_Y };
const P1 = { x: P0.x + 70, y: 18 };
const P2 = { x: P3.x - 70, y: 18 };
const LINK_D = `M ${P0.x} ${P0.y} C ${P1.x} ${P1.y}, ${P2.x} ${P2.y}, ${P3.x} ${P3.y}`;
const { lut: LUT, length: LINK_LEN } = buildLut(P0, P1, P2, P3);
const SLOTS = 10;
const TRAIL = [0, 0.02, 0.038];

const BAYS = [0, 1, 2].map(i => TOP + 64 + i * 26);
/** A unit's progress track: offset from the box's left edge, and width. */
const TRACK_X = 34;
const TRACK_W = BODY_W - 64;
/** The activity lights flicker out of step, as real ones do. */
const ACTIVITY = [
  { dur: 1.15, delay: 0 },
  { dur: 0.8, delay: 0.35 },
  { dur: 1.4, delay: 0.6 },
];
/** The link's colour along its length (for the packets), precomputed. */
const ARC_COLOURS = Array.from({ length: 51 }, (_, i) => arcColourAt(i / 50));
const arcColour = (f: number) => ARC_COLOURS[Math.round(Math.max(0, Math.min(1, f)) * 50)];
const GREEN = ARC_STOPS[2];
const CYAN = ARC_STOPS[1];

const blink = keyframes`
  0%, 100% { opacity: 0.35; }
  45% { opacity: 1; }
`;
const flicker = keyframes`
  0%, 100% { opacity: 1; }
  7% { opacity: 0.25; }
  14% { opacity: 1; }
  47% { opacity: 1; }
  52% { opacity: 0.3; }
  58% { opacity: 1; }
  80% { opacity: 0.55; }
  84% { opacity: 1; }
`;
const halo = keyframes`
  0% { transform: scale(1); opacity: 0.5; }
  100% { transform: scale(1.07); opacity: 0; }
`;
const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;
const dash = keyframes`
  from { stroke-dashoffset: 0; }
  to { stroke-dashoffset: -48; }
`;
const drawRing = keyframes`
  0% { stroke-dashoffset: 101; opacity: 1; }
  40%, 88% { stroke-dashoffset: 0; opacity: 1; }
  100% { stroke-dashoffset: 0; opacity: 0.2; }
`;
const drawTick = keyframes`
  0%, 38% { stroke-dashoffset: 26; }
  60%, 88% { stroke-dashoffset: 0; opacity: 1; }
  100% { stroke-dashoffset: 0; opacity: 0.2; }
`;
const breathe = keyframes`
  0%, 100% { opacity: 0.18; }
  50% { opacity: 0.4; }
`;
const sweep = keyframes`
  0% { transform: translateY(0); opacity: 0; }
  15% { opacity: 0.9; }
  85% { opacity: 0.9; }
  100% { transform: translateY(74px); opacity: 0; }
`;
const sheen = keyframes`
  from { transform: translateX(-20px); }
  to { transform: translateX(${TRACK_W + 20}px); }
`;
const arcBreathe = keyframes`
  0%, 100% { opacity: 0.32; }
  50% { opacity: 0.75; }
`;
const glowBreathe = keyframes`
  0%, 100% { opacity: 0.12; }
  50% { opacity: 0.3; }
`;
const arcSweep = keyframes`
  0% { stroke-dashoffset: 24; opacity: 0; }
  12% { opacity: 1; }
  80% { opacity: 1; }
  100% { stroke-dashoffset: -100; opacity: 0; }
`;
const ratePulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.035); }
`;
const capGlow = keyframes`
  0%, 100% { opacity: 0.45; transform: scale(0.94); }
  50% { opacity: 1; transform: scale(1.04); }
`;
const portPulse = keyframes`
  0% { transform: scale(0.7); opacity: 0.65; }
  100% { transform: scale(2.6); opacity: 0; }
`;

const Frame = styled.div<{ $small: boolean }>`
  position: relative;
  width: 100%;
  ${p =>
    p.$small
      ? css`
          max-width: 320px;
        `
      : css`
          max-width: 660px;
        `}
  svg.flow {
    display: block;
    width: 100%;
    height: auto;
    overflow: visible;
  }
  /* The vendor wordmarks come in grey for light and white for dark. */
  .wm-dark {
    display: none;
  }
  html[data-theme="dark"] & .wm-light {
    display: none;
  }
  html[data-theme="dark"] & .wm-dark {
    display: inline;
  }
  html[data-theme="dark"] & .mf-chip {
    fill: #eef3f6;
  }
  .led-read {
    animation: ${flicker} 1.1s steps(1, end) infinite;
  }
  /* (was: animation: blink 0.9s ease-in-out infinite) */
  .led-soft {
    animation: ${blink} 0.9s ease-in-out infinite;
  }
  .halo {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${halo} 1.8s ease-out infinite;
  }
  .gear {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${spin} 3.2s linear infinite;
  }
  .flowdash {
    animation: ${dash} 1.6s linear infinite;
  }
  .flowdash.slow {
    animation-duration: 4.8s;
  }
  .ring-build {
    stroke-dasharray: 101;
    animation: ${drawRing} 2.6s ease-in-out infinite;
  }
  .tick-build {
    stroke-dasharray: 26;
    animation: ${drawTick} 2.6s ease-in-out infinite;
  }
  .breathe {
    animation: ${breathe} 2.4s ease-in-out infinite;
  }
  .sweep {
    animation: ${sweep} 2.2s ease-in-out infinite;
  }
  .sheen {
    animation: ${sheen} 1.7s cubic-bezier(0.45, 0, 0.55, 1) infinite;
  }
  .arc-breathe {
    animation: ${arcBreathe} 2.8s ease-in-out infinite;
  }
  .glow-breathe {
    animation: ${glowBreathe} 2.8s ease-in-out infinite;
  }
  .arc-sweep {
    animation: ${arcSweep} 2.1s cubic-bezier(0.55, 0, 0.35, 1) infinite;
  }
  .rate-pulse {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${ratePulse} 2.6s ease-in-out infinite;
  }
  .cap-glow {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${capGlow} 2.6s ease-in-out infinite;
  }
  .port-pulse {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${portPulse} 2.4s ease-out infinite;
  }
  .bay-fill {
    transition:
      width 0.9s cubic-bezier(0.3, 1, 0.4, 1),
      fill 0.6s ease;
  }
  /* Off screen or in a hidden tab: hold every animation where it is. */
  &[data-paused="true"] * {
    animation-play-state: paused !important;
  }
  @media (prefers-reduced-motion: reduce) {
    .led-read,
    .led-soft,
    .halo,
    .gear,
    .flowdash,
    .ring-build,
    .tick-build,
    .breathe,
    .sweep,
    .sheen,
    .arc-breathe,
    .glow-breathe,
    .arc-sweep,
    .rate-pulse,
    .cap-glow,
    .port-pulse {
      animation: none;
    }
    .halo,
    .sweep,
    .sheen,
    .arc-sweep,
    .port-pulse {
      opacity: 0;
    }
    .arc-breathe {
      opacity: 0.6;
    }
    .glow-breathe {
      opacity: 0.2;
    }
  }
`;

type Props = {
  sourceType?: string | null;
  targetType?: string | null;
  phase: Phase;
  /** 0..100: fills the target's drive bays. */
  percent: number;
  /** Combined throughput now (bytes/s): packet speed and density. */
  rateBytesPerSec: number;
  activeDisks?: number;
  size?: "large" | "small";
  /** Labels under the machines and the figure under the arc (large only by default). */
  showLabels?: boolean;
  className?: string;
};

const clamp01 = (n: number) => Math.max(0, Math.min(1, n));

type Mode = "flow" | "probe" | "sweep" | "idle";

// Was (Finalising pinged like validating; it sweeps now):
// type Mode = "flow" | "probe" | "idle";
// const modeFor = (phase: Phase, rate: number): Mode => {
//   if (phase === "Syncing disks") {
//     return rate > 0 ? "flow" : "probe";
//   }
//   if (phase === "Validating" || phase === "Preparing" || phase === "Finalising") {
//     return "probe";
//   }
//   return "idle";
// };
const modeFor = (phase: Phase, rate: number): Mode => {
  if (phase === "Syncing disks") {
    return rate > 0 ? "flow" : "probe";
  }
  if (phase === "Validating" || phase === "Preparing") {
    return "probe";
  }
  if (phase === "Finalising") {
    return "sweep";
  }
  return "idle";
};

/** Rough width of a label in Montserrat semibold, for centring a mark beside it. */
const textWidth = (text: string, size: number) => text.length * size * 0.6;

const MigrationFlow = (props: Props) => {
  const {
    sourceType,
    targetType,
    phase,
    percent,
    rateBytesPerSec,
    activeDisks = 1,
    size = "large",
    className,
  } = props;
  const small = size === "small";
  const showLabels = props.showLabels != null ? props.showLabels : !small;
  const uid = React.useId().replace(/[^a-zA-Z0-9]/g, "");
  const id = (name: string) => `mf${uid}${name}`;

  const frameRef = React.useRef<HTMLDivElement>(null);
  const packetRefs = React.useRef<(SVGGElement | null)[]>([]);
  const probeRef = React.useRef<SVGGElement>(null);
  const absorbRef = React.useRef<SVGCircleElement>(null);
  const emitRef = React.useRef<SVGCircleElement>(null);
  const mode = modeFor(phase, rateBytesPerSec);
  const live = React.useRef({ mode, rate: rateBytesPerSec, disks: activeDisks });
  live.current = { mode, rate: rateBytesPerSec, disks: activeDisks };
  const reduced = React.useMemo(() => prefersReducedMotion(), []);

  React.useEffect(() => {
    ensureMarksSprite();
  }, []);

  React.useEffect(() => {
    const el = frameRef.current;
    const canAnimate = !reduced && typeof window.requestAnimationFrame === "function";
    let raf = 0;
    let last = 0;
    let offset = 0; // 0..1 along the link, shared by the slots
    let speed = 0; // px/s, eased towards the target speed
    let probe = 0; // 0..2 ping-pong for the handshake dot
    let probeAlpha = 0;
    let absorb = 0;
    let emit = 0;
    let onScreen = true;
    const alphas = new Array(SLOTS).fill(0);
    const lastF = new Array(SLOTS).fill(0);
    const hidden = () => typeof document !== "undefined" && document.hidden;
    const running = () => onScreen && !hidden();

    const frame = (now: number) => {
      const dt = last ? Math.min(0.05, (now - last) / 1000) : 0;
      last = now;
      const { mode: m, rate, disks } = live.current;
      const targetSpeed = m === "flow" ? packetSpeed(rate) : 0;
      speed += (targetSpeed - speed) * Math.min(1, dt * 2.2);
      offset = (offset + (speed * dt) / LINK_LEN) % 1;
      const count = m === "flow" ? packetCount(rate, disks, SLOTS) : 0;

      for (let i = 0; i < SLOTS; i += 1) {
        const g = packetRefs.current[i];
        if (!g) {
          continue;
        }
        const want = m === "flow" && slotVisible(i, count, SLOTS) ? 1 : 0;
        alphas[i] += (want - alphas[i]) * Math.min(1, dt * (want ? 3 : 1.6));
        const f = (offset + i / SLOTS) % 1;
        if (f < lastF[i] && alphas[i] > 0.3) {
          absorb = 1; // a packet reached the target
          emit = 1; // and the slot sets off again from the source
        }
        lastF[i] = f;
        const edge = Math.min(1, f / 0.06, (1 - f) / 0.07);
        const a = alphas[i] * clamp01(edge);
        g.style.opacity = a.toFixed(3);
        if (a < 0.01) {
          continue;
        }
        // The packet takes the link's colour where it is (steel → cyan → green).
        g.style.color = arcColour(f);
        // Children are drawn trail first, head last (on top).
        const parts = g.children;
        for (let e = 0; e < parts.length; e += 1) {
          const k = parts.length - 1 - e;
          const p = pointAt(LUT, f - TRAIL[k] * (0.6 + speed / 400));
          const lift = Math.sin(f * Math.PI) * 0.12 + 1;
          (parts[e] as SVGGraphicsElement).setAttribute(
            "transform",
            `translate(${p.x.toFixed(1)} ${p.y.toFixed(1)}) rotate(${p.angle.toFixed(1)}) scale(${(lift * (1 - k * 0.22)).toFixed(3)})`,
          );
        }
      }

      const pr = probeRef.current;
      if (pr) {
        const wantProbe = m === "probe" ? 1 : 0;
        probeAlpha += (wantProbe - probeAlpha) * Math.min(1, dt * 2.5);
        probe = (probe + dt * (95 / LINK_LEN)) % 2;
        const f = probe <= 1 ? probe : 2 - probe;
        const eased = f * f * (3 - 2 * f);
        const p = pointAt(LUT, eased);
        pr.setAttribute("transform", `translate(${p.x.toFixed(1)} ${p.y.toFixed(1)})`);
        pr.style.opacity = (probeAlpha * 0.9).toFixed(3);
        pr.style.color = arcColour(eased);
      }

      const ab = absorbRef.current;
      if (ab) {
        absorb = Math.max(0, absorb - dt * 2.4);
        ab.setAttribute("r", (5 + (1 - absorb) * 12).toFixed(2));
        ab.style.opacity = (absorb * 0.6).toFixed(3);
      }
      const em = emitRef.current;
      if (em) {
        emit = Math.max(0, emit - dt * 2.8);
        em.setAttribute("r", (4 + (1 - emit) * 9).toFixed(2));
        em.style.opacity = (emit * 0.45).toFixed(3);
      }
      raf = running() ? window.requestAnimationFrame(frame) : 0;
    };

    const startLoop = () => {
      if (canAnimate && !raf && running()) {
        last = 0;
        raf = window.requestAnimationFrame(frame);
      }
    };
    const stopLoop = () => {
      if (raf) {
        window.cancelAnimationFrame(raf);
        raf = 0;
      }
    };
    // The CSS animations follow the same switch (data-paused holds them).
    const sync = () => {
      if (el) {
        el.setAttribute("data-paused", running() ? "false" : "true");
      }
      if (running()) {
        startLoop();
      } else {
        stopLoop();
      }
    };

    let observer: IntersectionObserver | null = null;
    if (typeof IntersectionObserver !== "undefined" && el) {
      observer = new IntersectionObserver(entries => {
        onScreen = entries.some(e => e.isIntersecting);
        sync();
      });
      observer.observe(el);
    }
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", sync);
    }
    sync();
    return () => {
      stopLoop();
      if (observer) {
        observer.disconnect();
      }
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", sync);
      }
    };
  }, [reduced]);

  const done = phase === "Completed";
  const failed = phase === "Failed";
  const cancelled = phase === "Cancelled";
  const syncing = phase === "Syncing disks";
  const reading = syncing && rateBytesPerSec > 0;
  const osPrep = phase === "OS preparation";
  const deploying = phase === "Deploying";
  const finalising = phase === "Finalising";
  const afterSync = osPrep || deploying || done || finalising;
  const fill = done ? 1 : clamp01(percent / 100);
  // const fillColour = done || deploying ? `url(#${id("fillGreen")})` : `url(#${id("fillCyan")})`; (gradient fills, replaced by the accent)
  // const linkColour = done ? LIVE.slate : failed ? LIVE.faint : LIVE.cyan; (one colour; the link is a gradient now)

  // Static packets for reduced motion: a still frame of the flow.
  const stillPackets = reduced && mode === "flow" ? [0.18, 0.38, 0.58, 0.78] : [];

  /** The platform's mark at the top of its box: sprite mark + name, the vendor wordmark, or the name. */
  const platformHeader = (x: number, type: string | null | undefined, brand: string) => {
    const cx = x + BODY_W / 2;
    const name = providerLabel(type);
    const mark = platformMark(type);
    if (mark.kind === "wordmark") {
      return (
        <g>
          <title>{name}</title>
          <image className="wm-light" href={mark.light} x={cx - 50} y={TOP + 6} width={100} height={40} />
          <image className="wm-dark" href={mark.dark} x={cx - 50} y={TOP + 6} width={100} height={40} />
        </g>
      );
    }
    if (mark.kind === "sprite" || mark.kind === "onprem") {
      const chip = 28;
      const fontSize = 13;
      const total = chip + 8 + textWidth(name, fontSize);
      const left = Math.max(x + 10, cx - total / 2);
      const cy = TOP + 26;
      return (
        <g>
          <rect
            x={left}
            y={cy - chip / 2}
            width={chip}
            height={chip}
            rx={7}
            className="mf-chip"
            fill="#FFFFFF"
            stroke={brand}
            strokeOpacity={0.35}
          />
          {mark.kind === "sprite" ? (
            <svg x={left + 4} y={cy - chip / 2 + 4} width={chip - 8} height={chip - 8} viewBox="0 0 24 24">
              <use href={`#${mark.id}`} />
            </svg>
          ) : (
            <Server x={left + 6} y={cy - chip / 2 + 6} width={chip - 12} height={chip - 12} color={brand} strokeWidth={2} />
          )}
          <text x={left + chip + 8} y={cy + 4.5} fontSize={fontSize} fontWeight={700} fill={LIVE.text}>
            {name}
          </text>
        </g>
      );
    }
    return (
      <text x={cx} y={TOP + 31} textAnchor="middle" fontSize={13} fontWeight={700} fill={LIVE.text}>
        {name}
      </text>
    );
  };

  /*
   * A machine: a box tinted with its platform's brand colour, the platform's mark on top, and three
   * server units, each with vents, a disk track, a power light and an activity light. The source's
   * units being read carry a cyan sheen and flicker; the target's fill with the share copied
   * (bottom unit first), the one being written carrying the sheen.
   */
  const machine = (x: number, role: "source" | "target", type?: string | null) => {
    const isTarget = role === "target";
    const brand = platformColour(type);
    const reads = Math.max(1, Math.min(3, activeDisks || 1));
    return (
      <g>
        {role === "source" && reading ? (
          <rect
            className="halo"
            x={x - 4}
            y={TOP - 4}
            width={BODY_W + 8}
            height={BODY_H + 8}
            rx={18}
            fill="none"
            stroke={brand}
            strokeWidth={1.5}
          />
        ) : null}
        {isTarget && (done || deploying) ? (
          <rect
            className={done ? undefined : "breathe"}
            x={x - 4}
            y={TOP - 4}
            width={BODY_W + 8}
            height={BODY_H + 8}
            rx={18}
            fill="none"
            stroke={GREEN}
            strokeWidth={1.5}
            opacity={done ? 0.6 : undefined}
          />
        ) : null}
        <rect x={x} y={TOP} width={BODY_W} height={BODY_H} rx={14} fill={LIVE.surface} />
        <rect x={x} y={TOP} width={BODY_W} height={BODY_H} rx={14} fill={`url(#${id(`tint${role}`)})`} />
        <rect
          x={x}
          y={TOP}
          width={BODY_W}
          height={BODY_H}
          rx={14}
          fill="none"
          stroke={brand}
          strokeOpacity={0.6}
          strokeWidth={1.5}
        />
        {platformHeader(x, type, brand)}
        <line x1={x + 12} y1={TOP + 52} x2={x + BODY_W - 12} y2={TOP + 52} stroke={brand} strokeOpacity={0.3} strokeWidth={1.25} />
        {BAYS.map((y, i) => {
          // Bottom unit fills first: the fill level rises with the percentage.
          const share = clamp01(fill * 3 - (2 - i));
          const writing = isTarget && reading && share > 0 && share < 1;
          const scanning = !isTarget && reading && 2 - i < reads;
          const lit = (role === "source" && reading) || (isTarget && (syncing || afterSync) && share > 0);
          const stuck = failed && isTarget && share > 0 && share < 1;
          const actColour = stuck ? LIVE.danger : isTarget ? GREEN : CYAN;
          const flickers = writing || scanning;
          const tx = x + TRACK_X;
          const clip = id(`clip${role}${i}`);
          return (
            <g key={y}>
              <rect
                x={x + 12}
                y={y}
                width={BODY_W - 24}
                height={18}
                rx={5}
                fill={brand}
                fillOpacity={0.06}
                stroke={brand}
                strokeOpacity={0.38}
                strokeWidth={1.1}
              />
              {[0, 1, 2].map(k => (
                <rect key={k} x={x + 18 + k * 3.6} y={y + 5} width={1.5} height={8} rx={0.75} fill={brand} opacity={0.45} />
              ))}
              <rect x={tx} y={y + 6.5} width={TRACK_W} height={5} rx={2.5} fill={LIVE.track} />
              {isTarget ? (
                <rect
                  className="bay-fill"
                  x={tx}
                  y={y + 6.5}
                  width={Math.max(0, TRACK_W * share)}
                  height={5}
                  rx={2.5}
                  fill={`url(#${id(done ? "fillDone" : "fill")})`}
                  opacity={failed || cancelled ? 0.5 : 1}
                />
              ) : (
                <rect x={tx} y={y + 6.5} width={TRACK_W} height={5} rx={2.5} fill={brand} opacity={0.28} />
              )}
              {flickers ? (
                <g>
                  <clipPath id={clip}>
                    <rect x={tx} y={y + 6.5} width={isTarget ? TRACK_W * share : TRACK_W} height={5} rx={2.5} />
                  </clipPath>
                  <g clipPath={`url(#${clip})`}>
                    <rect
                      className="sheen"
                      style={{ animationDelay: `${i * 0.4}s` }}
                      x={tx}
                      y={y + 6.5}
                      width={20}
                      height={5}
                      fill={`url(#${id(isTarget ? "sheenW" : "sheenR")})`}
                    />
                  </g>
                </g>
              ) : null}
              <circle cx={x + BODY_W - 27} cy={y + 9} r={1.9} fill={brand} opacity={cancelled ? 0.35 : 0.85} />
              <circle
                className={flickers ? "led-read" : undefined}
                style={
                  flickers ? { animationDuration: `${ACTIVITY[i].dur}s`, animationDelay: `${ACTIVITY[i].delay}s` } : undefined
                }
                cx={x + BODY_W - 19}
                cy={y + 9}
                r={2.6}
                fill={lit || stuck ? actColour : LIVE.faint}
                opacity={lit || stuck ? 1 : 0.5}
              />
            </g>
          );
        })}
        {isTarget && (osPrep || finalising) ? (
          <rect
            className="sweep"
            x={x + 12}
            y={BAYS[0] - 2}
            width={BODY_W - 24}
            height={2}
            rx={1}
            fill={finalising ? GREEN : CYAN}
          />
        ) : null}
        {showLabels ? (
          <text
            x={x + BODY_W / 2}
            y={TOP + BODY_H + 26}
            textAnchor="middle"
            fontSize={11}
            fontWeight={600}
            letterSpacing="0.06em"
            fill={LIVE.muted}
          >
            {role === "source" ? "SOURCE" : "TARGET"}
          </text>
        ) : null}
      </g>
    );
  };

  // Was the line drawing in the portal's colours (UI spec, item 21), before the platform marks and colours:
  // /*
  //  * A machine as a clean line drawing in the portal's colours (UI spec, item 21): an outlined
  //  * frame with the provider's name, and three outlined drive units. The source's lights pulse while
  //  * it is read; the target's units fill with the share copied, in the accent.
  //  */
  // const machine = (x: number, role: "source" | "target", type?: string | null) => {
  //   const isTarget = role === "target";
  //   const accent = isTarget && (done || deploying) ? LIVE.slate : LIVE.cyan;
  //   return (
  //     <g>
  //       {role === "source" && reading ? (
  //         <rect
  //           className="halo"
  //           x={x - 4}
  //           y={TOP - 4}
  //           width={BODY_W + 8}
  //           height={BODY_H + 8}
  //           rx={18}
  //           fill="none"
  //           stroke={LIVE.cyan}
  //           strokeWidth={1.5}
  //         />
  //       ) : null}
  //       {isTarget && (done || deploying) ? (
  //         <rect
  //           className={done ? undefined : "breathe"}
  //           x={x - 4}
  //           y={TOP - 4}
  //           width={BODY_W + 8}
  //           height={BODY_H + 8}
  //           rx={18}
  //           fill="none"
  //           stroke={LIVE.slate}
  //           strokeWidth={1.5}
  //           opacity={done ? 0.45 : undefined}
  //         />
  //       ) : null}
  //       <rect
  //         x={x}
  //         y={TOP}
  //         width={BODY_W}
  //         height={BODY_H}
  //         rx={14}
  //         fill={LIVE.surface}
  //         stroke={LIVE.slate}
  //         strokeOpacity={0.55}
  //         strokeWidth={1.5}
  //       />
  //       <text
  //         x={x + BODY_W / 2}
  //         y={TOP + 36}
  //         textAnchor="middle"
  //         fontSize={13}
  //         fontWeight={600}
  //         fill={LIVE.textSecondary}
  //       >
  //         {providerLabel(type)}
  //       </text>
  //       <line x1={x + 12} y1={TOP + 52} x2={x + BODY_W - 12} y2={TOP + 52} stroke={LIVE.border} strokeWidth={1.5} />
  //       {BAYS.map((y, i) => {
  //         // Bottom unit fills first: the fill level rises with the percentage.
  //         const share = clamp01(fill * 3 - (2 - i));
  //         const lit = (role === "source" && reading) || (isTarget && (syncing || afterSync) && share > 0);
  //         return (
  //           <g key={y}>
  //             <rect
  //               x={x + 12}
  //               y={y}
  //               width={BODY_W - 24}
  //               height={18}
  //               rx={5}
  //               fill="none"
  //               stroke={LIVE.slate}
  //               strokeOpacity={0.45}
  //               strokeWidth={1.25}
  //             />
  //             <rect x={x + 34} y={y + 7} width={BODY_W - 64} height={4} rx={2} fill={LIVE.track} />
  //             {isTarget ? (
  //               <rect
  //                 className="bay-fill"
  //                 x={x + 34}
  //                 y={y + 7}
  //                 width={Math.max(0, (BODY_W - 64) * share)}
  //                 height={4}
  //                 rx={2}
  //                 fill={accent}
  //               />
  //             ) : null}
  //             <circle cx={x + 22} cy={y + 9} r={2.25} fill="none" stroke={LIVE.slate} strokeOpacity={0.6} strokeWidth={1.25} />
  //             <circle
  //               className={reading && role === "source" ? "led-read" : undefined}
  //               style={reading && role === "source" ? { animationDelay: `${i * 0.22}s` } : undefined}
  //               cx={x + BODY_W - 21}
  //               cy={y + 9}
  //               r={2.75}
  //               fill={lit ? accent : LIVE.faint}
  //               opacity={lit ? 1 : 0.5}
  //             />
  //           </g>
  //         );
  //       })}
  //       {isTarget && osPrep ? (
  //         <rect className="sweep" x={x + 12} y={BAYS[0] - 2} width={BODY_W - 24} height={2} rx={1} fill={LIVE.cyan} />
  //       ) : null}
  //       {showLabels ? (
  //         <text
  //           x={x + BODY_W / 2}
  //           y={TOP + BODY_H + 26}
  //           textAnchor="middle"
  //           fontSize={11}
  //           fontWeight={600}
  //           letterSpacing="0.06em"
  //           fill={LIVE.muted}
  //         >
  //           {role === "source" ? "SOURCE" : "TARGET"}
  //         </text>
  //       ) : null}
  //     </g>
  //   );
  // };

  // Was a dark slate server with a white logo plate and shadow; replaced by the line drawing above (item 21):
  //   const machine = (x: number, role: "source" | "target", type?: string | null) => {
  //     const isTarget = role === "target";
  //     const accent = isTarget && (done || deploying) ? LIVE.green : LIVE.cyan;
  //     return (
  //       <g>
  //         <ellipse cx={x + BODY_W / 2} cy={TOP + BODY_H + 12} rx={BODY_W * 0.46} ry={6} fill="var(--t9-text)" opacity={0.09} />
  //         {role === "source" && reading ? (
  //           <rect
  //             className="halo"
  //             x={x - 4}
  //             y={TOP - 4}
  //             width={BODY_W + 8}
  //             height={BODY_H + 8}
  //             rx={19}
  //             fill="none"
  //             stroke={LIVE.cyan}
  //             strokeWidth={1.5}
  //           />
  //         ) : null}
  //         {isTarget && (done || deploying) ? (
  //           <rect
  //             className={done ? undefined : "breathe"}
  //             x={x - 5}
  //             y={TOP - 5}
  //             width={BODY_W + 10}
  //             height={BODY_H + 10}
  //             rx={20}
  //             fill="none"
  //             stroke={LIVE.green}
  //             strokeWidth={3}
  //             opacity={done ? 0.35 : undefined}
  //           />
  //         ) : null}
  //         <rect x={x} y={TOP} width={BODY_W} height={BODY_H} rx={16} fill={`url(#${id("body")})`} />
  //         <rect x={x + 1.5} y={TOP + 1.5} width={BODY_W - 3} height={BODY_H - 3} rx={14.5} fill="none" stroke="#FFFFFF" strokeOpacity={0.12} />
  //         <rect x={x + 12} y={TOP + 12} width={BODY_W - 24} height={40} rx={8} fill="#FFFFFF" />
  //         <foreignObject x={x + 14} y={TOP + 16} width={BODY_W - 28} height={32}>
  //           <div style={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%", height: "32px" }}>
  //             <EndpointLogos endpoint={type || null} height={32} />
  //           </div>
  //         </foreignObject>
  //         {BAYS.map((y, i) => {
  //           // Bottom bay fills first: the fill level rises with the percentage.
  //           const share = clamp01(fill * 3 - (2 - i));
  //           return (
  //             <g key={y}>
  //               <rect x={x + 12} y={y} width={BODY_W - 24} height={18} rx={5} fill={LIVE.slateDeep} />
  //               {[0, 1, 2].map(k => (
  //                 <rect key={k} x={x + 18 + k * 4} y={y + 5} width={1.6} height={8} rx={0.8} fill="#5F7888" />
  //               ))}
  //               {isTarget ? (
  //                 <rect
  //                   className="bay-fill"
  //                   x={x + 34}
  //                   y={y + 6}
  //                   width={Math.max(0, (BODY_W - 64) * share)}
  //                   height={6}
  //                   rx={3}
  //                   fill={fillColour}
  //                 />
  //               ) : (
  //                 <rect x={x + 34} y={y + 8} width={BODY_W - 64} height={2} rx={1} fill="#43596A" />
  //               )}
  //               <circle
  //                 className={reading && role === "source" ? "led-read" : undefined}
  //                 style={reading && role === "source" ? { animationDelay: `${i * 0.22}s` } : undefined}
  //                 cx={x + BODY_W - 20}
  //                 cy={y + 9}
  //                 r={3}
  //                 fill={
  //                   (role === "source" && reading) || (isTarget && (syncing || afterSync) && share > 0)
  //                     ? accent
  //                     : "#5F7888"
  //                 }
  //               />
  //             </g>
  //           );
  //         })}
  //         {isTarget && osPrep ? (
  //           <rect className="sweep" x={x + 12} y={BAYS[0] - 2} width={BODY_W - 24} height={2} rx={1} fill={LIVE.cyan} />
  //         ) : null}
  //         <rect x={x + 26} y={TOP + BODY_H - 6} width={BODY_W - 52} height={2} rx={1} fill="#FFFFFF" opacity={0.14} />
  //         {showLabels ? (
  //           <text
  //             x={x + BODY_W / 2}
  //             y={TOP + BODY_H + 36}
  //             textAnchor="middle"
  //             fontSize={11}
  //             fontWeight={600}
  //             letterSpacing="0.06em"
  //             fill={LIVE.muted}
  //           >
  //             {`${role === "source" ? "SOURCE" : "TARGET"} · ${providerLabel(type).toUpperCase()}`}
  //           </text>
  //         ) : null}
  //       </g>
  //     );
  //   };

  const badgeX = DST_X + BODY_W - 6;
  const badgeY = TOP + 6;
  const badge = () => {
    if (osPrep) {
      return (
        <g>
          <circle cx={badgeX} cy={badgeY} r={17} fill={LIVE.surface} stroke={LIVE.slate} strokeWidth={1.5} />
          <g className="gear">
            <Settings x={badgeX - 11} y={badgeY - 11} width={22} height={22} color={LIVE.slate} strokeWidth={2} />
          </g>
        </g>
      );
    }
    if (deploying || done) {
      // Was in the brand steel; completed reads in Brand Green now (the end of the link's palette).
      return (
        <g>
          <circle cx={badgeX} cy={badgeY} r={17} fill={done ? `url(#${id("fillDone")})` : LIVE.surface} />
          <circle
            className={done ? undefined : "ring-build"}
            cx={badgeX}
            cy={badgeY}
            r={16}
            fill="none"
            stroke={done ? GREEN : LIVE.slate}
            strokeWidth={2.5}
            transform={`rotate(-90 ${badgeX} ${badgeY})`}
          />
          <path
            className={done ? undefined : "tick-build"}
            d={`M ${badgeX - 7} ${badgeY + 0.5} l 4.8 4.8 l 9.2 -9.8`}
            fill="none"
            stroke={done ? "#FFFFFF" : LIVE.slate}
            strokeWidth={3}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </g>
      );
    }
    if (failed) {
      return (
        <g>
          <circle cx={badgeX} cy={badgeY} r={17} fill={LIVE.danger} />
          <rect x={badgeX - 1.6} y={badgeY - 9} width={3.2} height={11} rx={1.6} fill="var(--t9-text-on-fill)" />
          <circle cx={badgeX} cy={badgeY + 6.5} r={2} fill="var(--t9-text-on-fill)" />
        </g>
      );
    }
    if (phase === "Queued") {
      return (
        <g>
          <circle cx={badgeX} cy={badgeY} r={17} fill={LIVE.surface} stroke={LIVE.faint} strokeWidth={1.5} />
          <Hourglass x={badgeX - 9} y={badgeY - 9} width={18} height={18} color={LIVE.muted} strokeWidth={2} />
        </g>
      );
    }
    return null;
  };

  const caption = () => {
    if (!showLabels) {
      return null;
    }
    let big = "";
    let smallText = "";
    if (reading) {
      big = formatRate(rateBytesPerSec);
      smallText = "MOVING NOW";
    } else if (syncing) {
      big = "Waiting for data";
      smallText = "SYNCING DISKS";
    } else if (osPrep) {
      big = "Preparing the OS";
      smallText = "DRIVERS · BOOT · NETWORK";
    } else if (deploying) {
      big = "Building the machine";
      smallText = "DEPLOYING";
    } else if (done) {
      big = "Done";
      smallText = "COMPLETED";
    } else if (failed) {
      big = "Stopped";
      smallText = "FAILED";
    } else if (cancelled) {
      big = "Cancelled";
      smallText = "";
    } else if (phase === "Queued") {
      big = "Waiting to start";
      smallText = "QUEUED";
    } else if (finalising) {
      big = "Finalising";
      smallText = "CLEANING UP";
    } else if (phase === "Validating") {
      big = "Validating";
      smallText = "CHECKING BOTH SIDES";
    } else {
      big = phase;
      smallText = "SETTING UP";
    }
    // The small line was text-faint (under 4.5:1 on white); it is text-secondary now (AA).
    // Capitals at 0.12em tracking run wider than mixed case.
    const dotX = W / 2 - (smallText.length * 10 * 0.84) / 2 - 8;
    return (
      <g>
        {reading ? (
          <ellipse className="cap-glow" cx={W / 2} cy={PORT_Y + 4} rx={92} ry={30} fill={`url(#${id("capGlow")})`} />
        ) : null}
        <g className={reading ? "rate-pulse" : undefined}>
          <text
            x={W / 2}
            y={PORT_Y + 8}
            textAnchor="middle"
            fontSize={reading ? 26 : 17}
            fontWeight={700}
            fill={failed ? LIVE.danger : LIVE.text}
            letterSpacing="-0.02em"
          >
            {big}
          </text>
        </g>
        {smallText ? (
          <g>
            {reading ? <circle className="led-soft" cx={dotX} cy={PORT_Y + 24.5} r={2.6} fill={CYAN} /> : null}
            {done ? <circle cx={dotX} cy={PORT_Y + 24.5} r={2.6} fill={GREEN} /> : null}
            <text x={W / 2} y={PORT_Y + 28} textAnchor="middle" fontSize={10} fontWeight={600} letterSpacing="0.12em" fill={LIVE.muted}>
              {smallText}
            </text>
          </g>
        ) : null}
      </g>
    );
  };

  // Was a gradient square with a glow filter; a plain accent dot reads cleaner.
  // const packet = (k: number) =>
  //   k === 0 ? (
  //     <circle r={4.5} fill={LIVE.cyan} />
  //   ) : (
  //     <rect x={-3.5} y={-3.5} width={7} height={7} rx={2.2} fill={LIVE.cyan} opacity={k === 1 ? 0.32 : 0.14} />
  //   );
  // A packet: a bright core in the link's colour (currentColor, set per frame) with a soft halo, and a
  // short trail of capsules.
  const packet = (k: number) =>
    k === 0 ? (
      <g>
        <circle r={9} fill="currentColor" opacity={0.16} />
        <circle r={6} fill="currentColor" opacity={0.28} />
        <circle r={4.2} fill="currentColor" />
        <circle r={1.5} fill="#FFFFFF" opacity={0.55} />
      </g>
    ) : (
      <rect x={-5} y={-2.4} width={10} height={4.8} rx={2.4} fill="currentColor" opacity={k === 1 ? 0.4 : 0.18} />
    );

  const label = `${providerLabel(sourceType)} to ${providerLabel(targetType)}: ${phase}${
    reading ? `, ${formatRate(rateBytesPerSec)}` : ""
  }, ${Math.round(done ? 100 : percent)}% copied`;

  const srcBrand = platformColour(sourceType);
  const dstBrand = platformColour(targetType);
  // How the link looks: flowing, breathing, sweeping, settled (solid), broken, or still.
  const arc = failed
    ? "broken"
    : done
      ? "done"
      : mode === "flow"
        ? "flow"
        : mode === "sweep"
          ? "sweep"
          : mode === "probe"
            ? "calm"
            : osPrep || deploying
              ? "settled"
              : "still";
  const gradient = `url(#${id("arc")})`;
  const mid = pointAt(LUT, 0.5);

  return (
    <Frame ref={frameRef} $small={small} className={className} role="img" aria-label={label} data-state={arc}>
      <svg className="flow" viewBox={`0 0 ${W} ${showLabels ? H : H - 22}`} aria-hidden="true">
        <defs>
          <linearGradient id={id("body")} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#5B7788" />
            <stop offset="1" stopColor={LIVE.slateDark} />
          </linearGradient>
          <linearGradient id={id("fillCyan")} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor={LIVE.cyanDeep} />
            <stop offset="1" stopColor={LIVE.cyan} />
          </linearGradient>
          <linearGradient id={id("fillGreen")} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor={LIVE.greenDeep} />
            <stop offset="1" stopColor={LIVE.green} />
          </linearGradient>
          <linearGradient id={id("packet")} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#A6E9FC" />
            <stop offset="1" stopColor={LIVE.cyan} />
          </linearGradient>
          <filter id={id("glow")} x="-150%" y="-150%" width="400%" height="400%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="3" result="blur" />
            <feColorMatrix
              in="blur"
              type="matrix"
              values="0 0 0 0 0.27  0 0 0 0 0.77  0 0 0 0 0.95  0 0 0 0.9 0"
              result="tint"
            />
            <feMerge>
              <feMergeNode in="tint" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          {/* The link: steel → Bright Cyan → Brand Green, source to target. */}
          <linearGradient id={id("arc")} gradientUnits="userSpaceOnUse" x1={P0.x} y1={0} x2={P3.x} y2={0}>
            <stop offset="0" stopColor={ARC_STOPS[0]} />
            <stop offset="0.5" stopColor={ARC_STOPS[1]} />
            <stop offset="1" stopColor={ARC_STOPS[2]} />
          </linearGradient>
          <filter id={id("soft")} filterUnits="userSpaceOnUse" x={P0.x - 30} y={0} width={P3.x - P0.x + 60} height={PORT_Y + 30}>
            <feGaussianBlur stdDeviation="3.5" />
          </filter>
          {/* The target's units: cyan filling into green, all green once complete. */}
          <linearGradient id={id("fill")} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor={ARC_STOPS[1]} />
            <stop offset="1" stopColor={ARC_STOPS[2]} />
          </linearGradient>
          <linearGradient id={id("fillDone")} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#8BD08D" />
            <stop offset="1" stopColor="#4FA752" />
          </linearGradient>
          <linearGradient id={id("sheenW")} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor="#FFFFFF" stopOpacity={0} />
            <stop offset="0.6" stopColor="#FFFFFF" stopOpacity={0.85} />
            <stop offset="1" stopColor="#FFFFFF" stopOpacity={0} />
          </linearGradient>
          <linearGradient id={id("sheenR")} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor={CYAN} stopOpacity={0} />
            <stop offset="0.6" stopColor={CYAN} stopOpacity={1} />
            <stop offset="1" stopColor={CYAN} stopOpacity={0} />
          </linearGradient>
          <radialGradient id={id("capGlow")}>
            <stop offset="0" stopColor={CYAN} stopOpacity={0.2} />
            <stop offset="0.6" stopColor={CYAN} stopOpacity={0.07} />
            <stop offset="1" stopColor={CYAN} stopOpacity={0} />
          </radialGradient>
          {/* Each box carries its platform's colour: stronger behind the mark, faint below. */}
          {(
            [
              ["source", srcBrand],
              ["target", dstBrand],
            ] as const
          ).map(([role, colour]) => (
            <linearGradient key={role} id={id(`tint${role}`)} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" style={{ stopColor: colour, stopOpacity: 0.16 }} />
              <stop offset="0.346" style={{ stopColor: colour, stopOpacity: 0.09 }} />
              <stop offset="0.347" style={{ stopColor: colour, stopOpacity: 0.035 }} />
              <stop offset="1" style={{ stopColor: colour, stopOpacity: 0.02 }} />
            </linearGradient>
          ))}
        </defs>

        {/* the link */}
        {/* Was one hairline in a single colour, dashed while moving:
        <path d={LINK_D} fill="none" stroke={LIVE.border} strokeWidth={2} strokeLinecap="round" />
        {mode !== "idle" || done || failed ? (
          <path
            className={mode === "flow" ? "flowdash" : mode === "probe" ? "flowdash slow" : undefined}
            d={LINK_D}
            fill="none"
            stroke={linkColour}
            strokeWidth={2}
            strokeLinecap="round"
            strokeDasharray={done ? undefined : "2 10"}
            opacity={done ? 0.9 : mode === "flow" ? 0.6 : 0.4}
          />
        ) : null}
        <circle cx={P0.x} cy={P0.y} r={4} fill={LIVE.surface} stroke={LIVE.slate} strokeWidth={1.5} />
        <circle cx={P3.x} cy={P3.y} r={4} fill={LIVE.surface} stroke={LIVE.slate} strokeWidth={1.5} />
        */}
        <path d={LINK_D} fill="none" stroke={LIVE.border} strokeWidth={3} strokeLinecap="round" />
        {arc === "flow" || arc === "sweep" || arc === "done" || arc === "calm" || arc === "settled" ? (
          <g>
            <path
              className={arc === "flow" || arc === "calm" ? "glow-breathe" : undefined}
              d={LINK_D}
              fill="none"
              stroke={gradient}
              strokeWidth={9}
              strokeLinecap="round"
              filter={`url(#${id("soft")})`}
              opacity={arc === "done" ? 0.22 : arc === "settled" ? 0.1 : 0.18}
            />
            <path
              className={arc === "calm" ? "arc-breathe" : undefined}
              d={LINK_D}
              fill="none"
              stroke={gradient}
              strokeWidth={2.75}
              strokeLinecap="round"
              opacity={arc === "flow" || arc === "done" ? 1 : arc === "sweep" ? 0.35 : arc === "settled" ? 0.55 : undefined}
            />
          </g>
        ) : null}
        {arc === "flow" ? (
          <path
            className="flowdash"
            d={LINK_D}
            fill="none"
            stroke={LIVE.surface}
            strokeWidth={3}
            strokeDasharray="2.5 13.5"
            opacity={0.55}
          />
        ) : null}
        {arc === "sweep" ? (
          <g>
            <g opacity={0.55}>
              <path
                className="arc-sweep"
                d={LINK_D}
                pathLength={100}
                fill="none"
                stroke={gradient}
                strokeWidth={12}
                strokeLinecap="round"
                strokeDasharray="24 100"
                filter={`url(#${id("soft")})`}
                opacity={0}
              />
            </g>
            <path
              className="arc-sweep"
              d={LINK_D}
              pathLength={100}
              fill="none"
              stroke={gradient}
              strokeWidth={4}
              strokeLinecap="round"
              strokeDasharray="24 100"
              opacity={0}
            />
          </g>
        ) : null}
        {arc === "broken" ? (
          <g>
            {/* What reached the break, in the palette, faded; beyond it grey; red at the break. */}
            <path d={LINK_D} pathLength={100} fill="none" stroke={gradient} strokeWidth={2.75} strokeDasharray="44 200" opacity={0.55} />
            <path d={LINK_D} pathLength={100} fill="none" stroke={LIVE.faint} strokeWidth={2} strokeDasharray="0 56 2 6 2 6 2 6 2 6 2 6 2 6 2 6" opacity={0.7} />
            <path
              d={LINK_D}
              pathLength={100}
              fill="none"
              stroke={LIVE.danger}
              strokeWidth={3}
              strokeLinecap="round"
              strokeDasharray="0 38 6 12 6 200"
            />
            <g transform={`translate(${mid.x} ${mid.y})`}>
              <circle r={9.5} fill={LIVE.danger} />
              <path d="M -3.6 -3.6 L 3.6 3.6 M 3.6 -3.6 L -3.6 3.6" stroke="#FFFFFF" strokeWidth={2.2} strokeLinecap="round" />
            </g>
          </g>
        ) : null}
        {arc === "still" ? (
          <path d={LINK_D} fill="none" stroke={LIVE.faint} strokeWidth={2} strokeLinecap="round" strokeDasharray="2 9" opacity={0.7} />
        ) : null}

        {/* the ports, in each side's colour; they pulse while the two sides are checked */}
        {arc === "calm" ? (
          <g>
            <circle className="port-pulse" cx={P0.x} cy={P0.y} r={5} fill="none" stroke={srcBrand} strokeWidth={1.5} />
            <circle
              className="port-pulse"
              style={{ animationDelay: "1.2s" }}
              cx={P3.x}
              cy={P3.y}
              r={5}
              fill="none"
              stroke={dstBrand}
              strokeWidth={1.5}
            />
          </g>
        ) : null}
        <circle ref={emitRef} cx={P0.x} cy={P0.y} r={4} fill="none" stroke={ARC_STOPS[0]} strokeWidth={2} style={{ opacity: 0 }} />
        <circle cx={P0.x} cy={P0.y} r={4.5} fill={arc === "done" || arc === "flow" ? srcBrand : LIVE.surface} stroke={srcBrand} strokeWidth={1.75} />
        <circle
          cx={P3.x}
          cy={P3.y}
          r={4.5}
          fill={arc === "done" ? GREEN : arc === "flow" ? dstBrand : LIVE.surface}
          stroke={arc === "done" ? GREEN : dstBrand}
          strokeWidth={1.75}
        />
        <circle ref={absorbRef} cx={P3.x} cy={P3.y} r={5} fill="none" stroke={GREEN} strokeWidth={2} style={{ opacity: 0 }} />

        {caption()}

        {/* packets: a glowing head and a short fading trail each */}
        {Array.from({ length: SLOTS }).map((_, i) => (
          <g
            key={i}
            ref={el => {
              packetRefs.current[i] = el;
            }}
            style={{ opacity: 0, color: CYAN }}
          >
            {TRAIL.map((__, k) => (
              <g key={k}>{packet(k)}</g>
            )).reverse()}
          </g>
        ))}
        {stillPackets.map(f => {
          const p = pointAt(LUT, f);
          return (
            <g key={f} transform={`translate(${p.x} ${p.y}) rotate(${p.angle})`} style={{ color: arcColour(f) }}>
              {packet(0)}
            </g>
          );
        })}
        <g ref={probeRef} style={{ opacity: 0, color: CYAN }}>
          <circle r={9} fill="currentColor" opacity={0.18} />
          <circle r={5.5} fill={LIVE.surface} stroke="currentColor" strokeWidth={2} />
          <circle r={2} fill="currentColor" />
        </g>

        {machine(SRC_X, "source", sourceType)}
        {machine(DST_X, "target", targetType)}
        {badge()}
      </svg>
    </Frame>
  );
};

export default MigrationFlow;
